import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CalendarToday,
  Person,
  Phone,
  LocationOn,
  AccessTime,
  School,
  Payment,
} from "@mui/icons-material";
import { getUserEnrollments } from "../../apis/programsApi";
import { ProgramEnrollment } from "../../schemas/programs";
import { format } from "date-fns/format";
import { formatDateForProgram } from "../../utils/DateUtils";
import { subscribeProgramEnrollmentConfirmed } from "../../utils/programEnrollmentEvents";
import { EnrollmentDetailsContent } from "../programs/EnrollmentDetailsContent";

const ProgramEnrollmentsSection: React.FC = () => {
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<ProgramEnrollment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchEnrollments = async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true;
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const data = await getUserEnrollments();
      setEnrollments(data);
    } catch (err) {
      console.error("Error fetching enrollments:", err);
      setError("Failed to load program enrollments");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, []);

  useEffect(() => {
    const refresh = () => {
      fetchEnrollments({ showLoading: false });
    };

    const unsubscribe = subscribeProgramEnrollmentConfirmed(() => {
      refresh();
    });

    const handleFocus = () => refresh();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
      case "confirmed":
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "pay_at_counter":
        return "warning";
      case "cancelled":
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const handleViewDetails = (enrollment: ProgramEnrollment) => {
    setSelectedEnrollment(enrollment);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEnrollment(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (enrollments.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <School sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Program Enrollments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You haven't enrolled in any programs yet. Check out our available programs!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        My Program Enrollments
      </Typography>

      <Grid container spacing={3}>
        {enrollments.map((enrollment) => (
          <Grid item xs={12} md={6} lg={4} key={enrollment.id}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: "bold" }}>
                    {enrollment.session?.program?.title || "Program"}
                  </Typography>
                  <Chip
                    label={enrollment.paymentStatus}
                    color={getStatusColor(enrollment.paymentStatus) as any}
                    size="small"
                  />
                </Box>

                <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                  {enrollment.session?.name || "Session"}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Person sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {enrollment.participantName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <CalendarToday sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDateForProgram(enrollment.selectedStartDate || "")} - {formatDateForProgram(enrollment.selectedEndDate || "")}
                    </Typography>
                  </Box>

                  {enrollment.timeSlot && (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {enrollment.timeSlot}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <School sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary">
                      Age Group: {enrollment.participantAge}
                    </Typography>
                  </Box>

                  {enrollment.totalAmount && (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Payment sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid: ${Number(enrollment.totalAmount)}
                      </Typography>
                    </Box>
                  )}

                  {enrollment.tax && (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Payment sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        Tax: {Number(enrollment.tax)}%
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleViewDetails(enrollment)}
                  sx={{ mt: "auto" }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h6" sx={{ fontWeight: "bold" }} component="div">
            Enrollment Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedEnrollment && <EnrollmentDetailsContent enrollment={selectedEnrollment} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramEnrollmentsSection;
